import axios from 'axios';
import produce from 'immer';
import { mapKeys } from 'lodash';

import { RESET_ALL as RESET_ON_LOGOUT, danger, success } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { createReducer } from 'utils/redux';
import { API, get, post } from 'utils/api';
import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';

import type {
  DeliverableType,
  Dispatch,
  ThunkAction
} from 'types';
import type { CalculatedBudgetSplitResource } from 'modules/reporting/types/BudgetSplitResource.type';

applyTranslations(require('./bulk-cost-attribution-reducer.i18n.json'));

const { REPORTS } = API.END_POINTS;

const SET_STATE = 'bulkCostAttribution/SET_STATE';
const SET_DATA = 'bulkCostAttribution/SET_DATA';
const RESET = 'bulkCostAttribution/RESET';

export type BulkCostAttributionLoadingState = 'initial' | 'loading' | 'loaded' | 'failed';

export type CostAttributionSlice = {
  taskTypes: Array<DeliverableType>,
  offerIds: Array<number>
}

type State = {
  currentState: BulkCostAttributionLoadingState,
  data: Array<CostAttributionSlice>
}

const initialState: State = {
  currentState: 'initial',
  data: []
};

function setState(currentState: BulkCostAttributionLoadingState) {
  return {
    type: SET_STATE,
    payload: {
      currentState
    }
  };
}

function setData(data: Array<CostAttributionSlice>) {
  return {
    type: SET_DATA,
    payload: {
      data: data.sort((a, b) => {
        // sort from most amount of creators to least
        const aLength = a.offerIds.length;
        const bLength = b.offerIds.length;

        if (aLength === bLength) {
          return 0;
        }

        return (aLength > bLength) ? -1 : 1;
      })
    }
  };
}

export function resetBulkCostAttribution() {
  return {
    type: RESET
  };
}

let fetchBulkCostAttributionCancelToken;

export function fetchBulkCostAttribution(campaignId: number): ThunkAction {
  return async (dispatch: Dispatch) => {
    dispatch(setState('loading'));

    if (fetchBulkCostAttributionCancelToken) {
      fetchBulkCostAttributionCancelToken.cancel();
    }

    fetchBulkCostAttributionCancelToken = axios.CancelToken.source();

    try {
      const url = REPORTS.offerGroups(campaignId);
      const results = await get(url, {
        cancelToken: fetchBulkCostAttributionCancelToken.token
      }, {
        suppressGlobalNotification: true
      });

      dispatch(setData(results.data.offersByType));
      dispatch(setState('loaded'));
    } catch (error) {
      if (!axios.isCancel(error)) {
        logError(error);
        dispatch(danger(__t('bulk_cost_attribution_reducer.fetch_error')));
        dispatch(setState('failed'));
      }
    }
  };
}

export type BulkCostAttributionsSet = {
  offerIds: Array<number>,
  budgetSplit: Record<string, number|string>
};

const TaskTypeMap = {
  additional: 'additional_costs'
};

function mapEntries(entries: Array<BulkCostAttributionsSet>): Array<BulkCostAttributionsSet> {
  return produce(entries, (draftState) => {
    draftState.forEach(entry => {
      // eslint-disable-next-line no-param-reassign
      entry.budgetSplit = mapKeys(entry.budgetSplit, (value, key) => TaskTypeMap[key] ?? key);
    });
  });
}

export function calculateBulkCostAttributions(campaignId: number, entries: Array<BulkCostAttributionsSet>): ThunkAction {
  return async (dispatch: Dispatch): Promise<Array<CalculatedBudgetSplitResource>> => {
    const url = REPORTS.bulkBudgetSplits(campaignId);

    dispatch(showLoading(url));

    try {
      const { data } = await post(url, { data: mapEntries(entries) }, { suppressGlobalNotification: true });
      dispatch(success(__t('bulk_cost_attribution_reducer.bulk_set_success')));
      return data;
    } catch (error) {
      dispatch(danger(__t('bulk_cost_attribution_reducer.bulk_set_error')));
      logError(error);
      return [];
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export default createReducer<State, any>('bulkCostAttribution', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [SET_DATA]: (state, { data }) => ({
    ...state,
    data
  }),
  [SET_STATE]: (state, { currentState }) => ({
    ...state,
    currentState
  })
});
